/**
 * Run work after first paint / when the browser is idle.
 *
 * Prefers requestIdleCallback with a timeout cap; falls back to setTimeout.
 * In NODE_ENV=test, queues on the next macrotask (setTimeout 0) so unit tests
 * stay fast and deterministic without fake timers.
 * Returns a cancel function.
 *
 * Example: scheduleDeferred(() => refreshBadge(), 2000)
 */
export function scheduleDeferred(fn: () => void, timeoutMs = 2000): () => void {
  if (typeof window === 'undefined') {
    fn();
    return () => undefined;
  }

  if (process.env.NODE_ENV === 'test') {
    const timeoutId = window.setTimeout(fn, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }

  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  if (typeof idleWindow.requestIdleCallback === 'function') {
    const id = idleWindow.requestIdleCallback(() => fn(), { timeout: timeoutMs });
    return () => {
      idleWindow.cancelIdleCallback?.(id);
    };
  }

  const timeoutId = window.setTimeout(fn, timeoutMs);
  return () => {
    window.clearTimeout(timeoutId);
  };
}
